export type ApInvoicePurchaseOrderTracking = {
  tracking_number: string;
  source: string;
  source_label: string;
};

export const AP_INVOICE_TRACKING_HOVER_HINT =
  "Tracking printed on the vendor invoice appears here after AI review, or add numbers manually. Pick additional numbers from the purchase order section below. Green means AI read the same number from the invoice PDF and it matches the purchase order.";

/** Correct common OCR misreads on UPS tracking numbers (for example 12 prefix instead of 1Z). */
export function canonicalizeApInvoiceTrackingNumber(trackingNumber: string): string {
  const trimmed = trackingNumber.trim();
  if (trimmed === "") return trimmed;
  const upper = trimmed.toUpperCase();
  if (/^12[0-9A-Z]{16,22}$/.test(upper)) {
    return `1Z${upper.slice(2)}`;
  }
  return trimmed;
}

export function trackingNumbersEquivalent(left: string, right: string): boolean {
  const a = left.trim();
  const b = right.trim();
  if (a === "" || b === "") return false;
  if (a.toLowerCase() === b.toLowerCase()) return true;
  return canonicalizeApInvoiceTrackingNumber(a) === canonicalizeApInvoiceTrackingNumber(b);
}

export function preferApInvoiceTrackingNumber(left: string, right: string): string {
  const canonicalLeft = canonicalizeApInvoiceTrackingNumber(left);
  const canonicalRight = canonicalizeApInvoiceTrackingNumber(right);
  if (canonicalLeft !== canonicalRight) {
    return left.trim() !== "" ? left.trim() : right.trim();
  }
  if (canonicalLeft.startsWith("1Z")) {
    return canonicalLeft;
  }
  return left.trim().length >= right.trim().length ? left.trim() : right.trim();
}

function dedupeTrackingNumbers(numbers: string[]): string[] {
  const out: string[] = [];
  for (const tracking of numbers) {
    const trimmed = tracking.trim();
    if (trimmed === "") continue;
    const existingIndex = out.findIndex((entry) => trackingNumbersEquivalent(entry, trimmed));
    if (existingIndex >= 0) {
      out[existingIndex] = preferApInvoiceTrackingNumber(out[existingIndex], trimmed);
      continue;
    }
    out.push(preferApInvoiceTrackingNumber(trimmed, canonicalizeApInvoiceTrackingNumber(trimmed)));
  }
  return out;
}

export function parseApInvoiceTrackingNumbers(value: unknown): string[] {
  const pieces: string[] = [];
  if (value == null || value === "") return [];
  if (Array.isArray(value)) {
    for (const entry of value) {
      if (typeof entry === "string" || typeof entry === "number") pieces.push(String(entry));
    }
  } else if (typeof value === "string" || typeof value === "number") {
    pieces.push(String(value));
  } else {
    return [];
  }

  const tokens: string[] = [];
  for (const piece of pieces) {
    for (const token of piece.split(/[/;,|\n\r]+/)) {
      const trimmed = token.trim();
      if (trimmed === "") continue;
      tokens.push(trimmed);
    }
  }
  return dedupeTrackingNumbers(tokens);
}

export function trackingNumberIsSelected(selected: string[], trackingNumber: string): boolean {
  return selected.some((entry) => trackingNumbersEquivalent(entry, trackingNumber));
}

export function displayApInvoiceTrackingNumber(
  trackingNumber: string,
  selected: string[],
): string {
  const match = selected.find((entry) => trackingNumbersEquivalent(entry, trackingNumber));
  return match ?? trackingNumber;
}

export function visiblePurchaseOrderTrackingCandidates(
  candidates: ApInvoicePurchaseOrderTracking[],
): ApInvoicePurchaseOrderTracking[] {
  return candidates.filter((candidate, index, all) => {
    return !all.some(
      (other, otherIndex) =>
        otherIndex !== index &&
        trackingNumbersEquivalent(candidate.tracking_number, other.tracking_number) &&
        preferApInvoiceTrackingNumber(other.tracking_number, candidate.tracking_number) !==
          candidate.tracking_number,
    );
  });
}

export function purchaseOrderTrackingCandidatesNotOnInvoice(
  candidates: ApInvoicePurchaseOrderTracking[],
  selected: string[],
): ApInvoicePurchaseOrderTracking[] {
  return visiblePurchaseOrderTrackingCandidates(candidates).filter(
    (candidate) => !trackingNumberIsSelected(selected, candidate.tracking_number),
  );
}

export function resolveApInvoiceTrackingChipPresentation(input: {
  trackingNumber: string;
  selected: string[];
  extractedFromInvoice: string[];
  purchaseOrderCandidates: ApInvoicePurchaseOrderTracking[];
}): {
  displayNumber: string;
  crossConfirmed: boolean;
  fromInvoice: boolean;
  purchaseOrderSourceLabel: string | null;
} {
  const displayNumber = displayApInvoiceTrackingNumber(input.trackingNumber, input.selected);
  const fromInvoice = input.extractedFromInvoice.some((entry) =>
    trackingNumbersEquivalent(entry, input.trackingNumber),
  );
  const purchaseOrderMatch = visiblePurchaseOrderTrackingCandidates(input.purchaseOrderCandidates).find(
    (candidate) => trackingNumbersEquivalent(candidate.tracking_number, input.trackingNumber),
  );
  return {
    displayNumber,
    fromInvoice,
    purchaseOrderSourceLabel: purchaseOrderMatch?.source_label ?? null,
    crossConfirmed: fromInvoice && purchaseOrderMatch != null,
  };
}

export function apInvoiceTrackingChipSourceLabel(input: {
  crossConfirmed: boolean;
  fromInvoice: boolean;
  purchaseOrderSourceLabel: string | null;
}): string {
  if (input.crossConfirmed) {
    return `Invoice + ${input.purchaseOrderSourceLabel ?? "purchase order"}`;
  }
  if (input.fromInvoice) {
    return "From invoice";
  }
  if (input.purchaseOrderSourceLabel != null) {
    return `Purchase order · ${input.purchaseOrderSourceLabel}`;
  }
  return "Manual";
}

export function toggleApInvoiceTrackingNumber(selected: string[], trackingNumber: string): string[] {
  const parsed = parseApInvoiceTrackingNumbers(trackingNumber);
  if (parsed.length === 0) return selected;
  let next = [...selected];
  for (const tracking of parsed) {
    if (trackingNumberIsSelected(next, tracking)) {
      next = next.filter((entry) => !trackingNumbersEquivalent(entry, tracking));
    } else {
      next.push(tracking);
    }
  }
  return parseApInvoiceTrackingNumbers(next);
}
